import { Trans } from '@lingui/react';
import { Card } from '@startuptools/ui/Card';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { OptionProgramsTable } from '../common/OptionPrograms';
import { useWarrantProgramListViewSuspenseQuery } from '../common/warrant-programs.graphql';
import React, { Suspense } from 'react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { GqlWarrantProgramKind, useCreateWarrantProgramMutation } from '../../../graphql/react-operations';
import { CreateWarrantProgramForm } from '../warrants/CreateWarrantProgram';
import { GenericErrorText } from '@startuptools/ui/Error';
import QesoPrereqTest from './components/prereq-test';
import { Modal } from '@startuptools/ui/joy/Modal';
import { DialogTitle, DialogContent, DialogActions } from '@startuptools/ui/Dialog';
import { Alert } from '@startuptools/ui/joy/Alert';
import { Button } from '@startuptools/ui/joy/Button';
import { ModalClose } from '@startuptools/ui/joy/ModalClose';
import { ModalDialog } from '@startuptools/ui/joy/ModalDialog';
import { GqlPaymentAccountKind } from '../../../graphql/base-types.graphql';
import { Route, Routes } from 'react-router-dom';
import { QesoProgramRoutes } from './QesoProgram';
import { useNavigate } from '../../../components/Navigate';

export const QesoProgramsRoutes = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Suspense>
            <QesoPrograms />
          </Suspense>
        }
      />
      <Route path=":warrantProgramId" element={<QesoProgramRoutes />}>
        <Route path="*" element={<QesoProgramRoutes />} />
      </Route>
    </Routes>
  );
};

type DialogState = false | 'prompt-test' | 'test' | 'create';
type State = { dialog: DialogState; usesStepper: boolean };
type Action = { type: 'prompt-test' | 'test' | 'hide' } | { type: 'create'; usesStepper: boolean };

const QesoPrograms = () => {
  const companyId = useCompanyId();
  const [status, setStatus] = React.useState<'active' | 'archived'>('active');
  const [didTest, setDidTest] = React.useState(false);
  const { navigate } = useNavigate();
  const [create, { error, loading }] = useCreateWarrantProgramMutation();
  const [state, dispatch] = React.useReducer<State, [Action]>(
    (state, action) => {
      switch (action.type) {
        case 'prompt-test':
        case 'test':
          return { ...state, dialog: action.type };
        case 'create':
          return { ...state, dialog: action.type, usesStepper: action.usesStepper };
        case 'hide':
          return { ...state, dialog: false };
        default:
          return state;
      }
    },
    { dialog: false, usesStepper: false },
  );

  const isDuplicateName = error?.message === 'description already in use';

  const { data, refetch } = useWarrantProgramListViewSuspenseQuery({
    variables: {
      companyId,
      kinds: [GqlWarrantProgramKind.Employee],
      fetchBonds: false,
      archived: status === 'archived',
    },
    fetchPolicy: 'cache-and-network',
  });

  React.useEffect(() => {
    void refetch();
  }, [refetch, status]);

  return (
    <>
      <Card sx={{ overflow: 'auto' }}>
        <Stack gap={2}>
          <Typography level="h2">
            <Trans id="Employee options" />
          </Typography>

          <Stack direction="row" gap={1}>
            <Button
              onClick={() => {
                dispatch({ type: 'prompt-test' });
              }}
            >
              {data.warrantPrograms.totalCount ? (
                <Trans id="Create new employee option program" />
              ) : (
                <Trans id="Create your first!" />
              )}
            </Button>
            <Button variant="outlined" color="neutral" onClick={() => dispatch({ type: 'create', usesStepper: false })}>
              <Trans id="Add previous program" />
            </Button>
          </Stack>

          <OptionProgramsTable
            warrantPrograms={data.warrantPrograms.models}
            totalCount={data.warrantPrograms.totalCount}
            status={status}
            setStatus={setStatus}
            warrantKind={GqlWarrantProgramKind.Employee}
          />
        </Stack>
      </Card>
      {state.dialog && (
        <Modal open onClose={() => dispatch({ type: 'hide' })}>
          <ModalDialog size="lg" sx={{ minWidth: '650px', overflow: 'auto' }}>
            <ModalClose />

            <React.Suspense>
              <DialogTitle>
                <Trans id="Create employee option program" />
              </DialogTitle>
              {state.dialog === 'prompt-test' && (
                <>
                  <DialogContent>
                    <Typography level="body-sm">
                      <Trans id="Some requirements have to be fulfilled to issue qualified employee stock options, do this test to see if the company is eligible." />
                    </Typography>
                    <Typography level="body-sm">
                      <Trans id="Do you want to test if your company matches the criteria to issue qualified employee stock options?" />
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="outlined" color="neutral" onClick={() => dispatch({ type: 'hide' })}>
                      <Trans id="Cancel" />
                    </Button>
                    <Button
                      variant="outlined"
                      color="neutral"
                      onClick={() => dispatch({ type: 'create', usesStepper: true })}
                    >
                      <Trans id="No, skip this" />
                    </Button>
                    <Button onClick={() => dispatch({ type: 'test' })}>
                      <Trans id="Do the test" />
                    </Button>
                  </DialogActions>
                </>
              )}
              {state.dialog === 'test' && (
                <QesoPrereqTest
                  startCreation={() => {
                    setDidTest(true);
                    dispatch({ type: 'create', usesStepper: true });
                  }}
                  onClose={() => dispatch({ type: 'hide' })}
                  company={data.company}
                />
              )}
              {state.dialog === 'create' && (
                <>
                  {didTest && (
                    <Alert color="success">
                      <Trans id="Your company is eligible to issue qualified employee stock options" />
                    </Alert>
                  )}
                  <CreateWarrantProgramForm
                    onClose={() => {
                      dispatch({ type: 'hide' });
                    }}
                    loading={loading}
                    error={
                      isDuplicateName ? (
                        <Trans id="A warrant program with this description already exists" />
                      ) : error ? (
                        <GenericErrorText />
                      ) : undefined
                    }
                    type="qeso"
                    onSave={async ({ description }) => {
                      const res = await create({
                        variables: {
                          companyId,
                          input: {
                            description,
                            warrantKind: GqlWarrantProgramKind.Employee,
                            useRiksbankenRate: false,
                            paymentAccountKind: GqlPaymentAccountKind.Other,
                            illiquidityDiscount: '0',
                            usesStepper: state.usesStepper,
                            discountPricePerWarrant: '0',
                            primaryLang: true,
                            secondaryLang: false,
                            antlerWise: false,
                            warrantsForQeso: false,
                            shareClasses: [],
                          },
                        },
                      });
                      const wp = res?.data?.createWarrantProgram;
                      if (wp?.id) {
                        void navigate(`/stock-options/employee-options/${wp.id}/edit`);
                      }
                    }}
                  />
                </>
              )}
            </React.Suspense>
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};
